import type React from "react";
import type { IBridgeInstance } from "./types";

interface IWebpackMFContainer {
  init: (shareScope: Record<string, unknown>) => Promise<void>;
  get: (module: string) => Promise<() => unknown>;
}

interface IWebpackMFWindow extends Window {
  __webpack_init_sharing__?: (scope: string) => Promise<void>;
  __webpack_share_scopes__?: {
    default?: Record<string, unknown>;
  };
}

function getWebpackContainer(
  containerName: string,
): IWebpackMFContainer | undefined {
  const globalScope = globalThis as unknown as Record<
    string,
    IWebpackMFContainer | undefined
  >;
  const container = globalScope[containerName];
  if (
    container &&
    typeof container.get === "function" &&
    typeof container.init === "function"
  ) {
    return container;
  }
  return undefined;
}

function resolveScriptUrl(src: string): string {
  return new URL(src, window.location.origin).href;
}

function findRemoteEntryScript(src: string): HTMLScriptElement | undefined {
  const absoluteSrc = resolveScriptUrl(src);
  return Array.from(document.querySelectorAll("script")).find(
    (script) =>
      script.src === absoluteSrc || script.getAttribute("src") === src,
  );
}

function loadScriptOnce(src: string, containerName: string): Promise<void> {
  const existingContainer = getWebpackContainer(containerName);
  if (existingContainer) {
    return Promise.resolve();
  }

  const existingScript = findRemoteEntryScript(src);
  if (existingScript) {
    existingScript.remove();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (getWebpackContainer(containerName)) {
        resolve();
        return;
      }
      reject(
        new Error(
          `Remote entry loaded from ${src} but global "${containerName}" was not registered`,
        ),
      );
    };
    script.onerror = () =>
      reject(new Error(`Failed to load remote entry: ${src}`));
    document.head.appendChild(script);
  });
}

async function initWebpackContainer(
  container: IWebpackMFContainer,
): Promise<void> {
  const win = window as IWebpackMFWindow;

  try {
    if (win.__webpack_init_sharing__ && win.__webpack_share_scopes__?.default) {
      await win.__webpack_init_sharing__("default");
      await container.init(win.__webpack_share_scopes__.default);
      return;
    }
  } catch (error) {
    console.warn(
      "Webpack share scope init failed, using isolated scope",
      error,
    );
  }

  await container.init({});
}

function normalizeExposePath(expose: string): string {
  return expose.startsWith("./") ? expose : `./${expose}`;
}

export function isBridgeInstance(value: unknown): value is IBridgeInstance {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as IBridgeInstance).render === "function" &&
    typeof (value as IBridgeInstance).destroy === "function"
  );
}

/**
 * Some remotes export a bridge API ({ render, destroy }) directly or via a factory
 * function instead of a React component.
 */
export function resolveBridgeFromModule(
  module: unknown,
  invokeProps?: Record<string, unknown>,
): IBridgeInstance | null {
  if (isBridgeInstance(module)) {
    return module;
  }

  const props = invokeProps ?? { isEmbedded: true };

  if (typeof module === "function") {
    try {
      const invoked = module(props);
      if (isBridgeInstance(invoked)) {
        return invoked;
      }
    } catch {
      // Not a bridge factory
    }
    return null;
  }

  if (typeof module === "object" && module !== null) {
    const record = module as Record<string, unknown>;

    if (isBridgeInstance(record.default)) {
      return record.default as IBridgeInstance;
    }

    if (typeof record.default === "function") {
      try {
        const invoked = record.default(props);
        if (isBridgeInstance(invoked)) {
          return invoked;
        }
      } catch {
        // Not a bridge factory
      }
    }
  }

  return null;
}

export function resolveRemoteComponent(module: unknown): React.ComponentType<{
  isEmbedded?: boolean;
  basename?: string;
}> | null {
  if (!module) {
    return null;
  }

  if (typeof module === "function") {
    return module as React.ComponentType<{
      isEmbedded?: boolean;
      basename?: string;
    }>;
  }

  if (typeof module === "object") {
    const record = module as Record<string, unknown>;
    if (typeof record.default === "function") {
      return record.default as React.ComponentType<{
        isEmbedded?: boolean;
        basename?: string;
      }>;
    }
  }

  return null;
}

export async function loadClassicWebpackRemote(
  remoteEntryUrl: string,
  containerName: string,
  expose: string,
): Promise<unknown> {
  await loadScriptOnce(remoteEntryUrl, containerName);

  const container = getWebpackContainer(containerName);
  if (!container) {
    throw new Error(
      `Webpack MF container "${containerName}" was not found after loading ${remoteEntryUrl}`,
    );
  }

  await initWebpackContainer(container);

  const factory = await container.get(normalizeExposePath(expose));
  return factory();
}
